<script lang="ts">
  import { isFullHeightWmsPath } from '$lib/wms/workspace-layout';
  import { _, waitLocale } from 'svelte-i18n';
  import { auth, isAuthenticated, isInitialized, currentUser, isImpersonating } from '$lib/stores/auth';
  import { isLoading } from '$lib/stores/loading';
  import { onMount } from 'svelte';
  import { beforeNavigate, goto } from '$app/navigation';
  import { page, updated } from '$app/state';
  import { browser } from '$app/environment';
  import { ModeWatcher } from 'mode-watcher';
  import { Toaster } from '$lib/components/ui/sonner/index.js';
  import '../app.css';
  import '$lib/i18n';
  import Navigation from '$lib/components/Navigation.svelte';
  import HeaderPreferencesMenu from '$lib/components/HeaderPreferencesMenu.svelte';
  import AppVersionDetails from '$lib/components/layout/AppVersionDetails.svelte';
  import * as Sidebar from '$lib/components/ui/sidebar/index.js';
  import { buildLoginRedirectUrl } from '$lib/utils/auth-route-resume';
  import { createAuthResumeCoordinator } from '$lib/stores/auth-resume';
  import { get } from 'svelte/store';
  import WmsAppRuntime from '$lib/components/wms/app/WmsAppRuntime.svelte';
  import WmsAppShell from '$lib/components/wms/app/WmsAppShell.svelte';
  import WmsAppActions from '$lib/components/wms/app/WmsAppActions.svelte';
  import { wmsApp } from '$lib/wms/app/wms-app-state.svelte';
  import { isWmsPath } from '$lib/wms/app/wms-app';
  import { canReloadWmsPage, wmsUpdateSafety } from '$lib/wms/app/wms-update-safety';

  const { children } = $props();

  let i18nReady = $state(false);
  let sidebarOpen = $state(true);
  let lastSidebarPath = $state<string | null>(null);
  onMount(() => { window.__appStartup?.markReady(); });
  const isFullHeightWmsRoute = $derived(isFullHeightWmsPath(page.url.pathname));

  $effect(() => {
    if (browser) {
      waitLocale().then(() => {
        i18nReady = true;
      });
    }
  });

  onMount(() => createAuthResumeCoordinator({
    document,
    window,
    refresh: () => auth.refreshAccessToken(),
    isInitialized: () => $isInitialized,
    isAuthenticated: () => $isAuthenticated
  }).destroy);

  beforeNavigate(({ willUnload, to }) => {
    if (updated.current && !willUnload && to?.url) {
      if (isWmsPath(page.url.pathname)
        && !canReloadWmsPage(page.url.pathname, get(wmsUpdateSafety))) return;
      window.location.href = to.url.href;
    }
  });

  $effect(() => {
    if (!browser || !$isInitialized) return;

    const currentPath = page.url.pathname;
    const needsAuth = !isPublicRoute(currentPath);

    if (needsAuth && !$isAuthenticated && routeExists(currentPath)) {
      isLoading.set(false);
      goto(buildLoginRedirectUrl(page.url, sessionStorage));
    }
  });

  $effect(() => {
    if (!browser || !$isInitialized || !$isAuthenticated) {
      return;
    }

    const currentPath = page.url.pathname;
    const enteringProductCms = currentPath.startsWith('/produkt-cms') && !(lastSidebarPath?.startsWith('/produkt-cms'));

    if (enteringProductCms) {
      sidebarOpen = false;
    }

    lastSidebarPath = currentPath;
  });

  function isPublicRoute(path: string): boolean {
    const publicRoutes = ['/login', '/register', '/forgot-password', '/reset-password', '/check-email', '/link-service'];
    return publicRoutes.some(route => path.startsWith(route));
  }

  function routeExists(_path: string): boolean {
    return true;
  }

</script>

<ModeWatcher modeStorageKey="theme" />
<Toaster />
<WmsAppRuntime />

{#if !i18nReady || !$isInitialized}
  <div class="flex min-h-screen items-center justify-center bg-white dark:bg-gray-900">
    <div class="h-8 w-8 animate-spin rounded-full border-y-2 border-gray-900 dark:border-gray-100"></div>
  </div>
{:else}
  <div class="flex h-screen flex-col bg-white dark:bg-gray-900">
    {#if $isAuthenticated}
      {#if $currentUser && $isImpersonating}
        <div class="bg-yellow-500 p-2 text-center text-black">
          {$_('profile.impersonating', { default: 'Du er pålogget som administrator' })}
          {#if $currentUser.companyName}
            {$_('profile.for', { default: 'for' })} {$currentUser.companyName}
          {/if}
          {#if import.meta.env.VITE_INCLUDE_SUPERADMIN === 'true'}
            <a href="/superadmin" class="underline hover:text-gray-800">
              {$_('profile.backToAdmin', { default: 'Gå tilbake til administrasjonspanelet' })}
            </a>
          {/if}
        </div>
      {/if}

      {#if /^\/dashboard\/screens\/[^/]+\/play\/?$/.test(page.url.pathname)}
        {@render children()}
      {:else if wmsApp.active}
        <WmsAppShell>{@render children()}</WmsAppShell>
      {:else}
      <Sidebar.Provider bind:open={sidebarOpen}>
        <div class="flex min-h-0 flex-1 overflow-hidden">
          <Navigation onLogout={() => auth.logout()} />

          <Sidebar.Inset class="flex min-h-0 flex-1 overflow-hidden">
            <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
              <header class="border-b border-border bg-background">
                <div class="mx-auto flex w-full items-center justify-between px-4 py-2 sm:px-6 lg:px-8">
                  <div class="flex items-center gap-2">
                    <Sidebar.Trigger
                      class="h-8 w-8 rounded-md border border-border bg-background shadow-sm hover:bg-muted"
                      title={$_('menu.toggleSidebar', { default: 'Vis eller skjul meny' })}
                      aria-label={$_('menu.toggleSidebar', { default: 'Vis eller skjul meny' })}
                    />
                    <span class="hidden text-xs font-medium text-muted-foreground sm:inline">
                      {$_('menu.navigation', { default: 'Meny' })}
                    </span>
                    <span class="text-sm font-medium text-gray-700 dark:text-gray-200">{$_('app.name')}</span>
                    <AppVersionDetails />
                    {#if isWmsPath(page.url.pathname)}<WmsAppActions />{/if}
                  </div>
                </div>
              </header>

              <div data-route-scroll-host="true" class={`flex-1 bg-gray-50 dark:bg-gray-900 ${isFullHeightWmsRoute ? 'overflow-hidden sm:overflow-auto' : 'overflow-auto'}`}>
                <div class={isFullHeightWmsRoute ? 'mx-auto h-full min-h-0 px-0 py-0 sm:px-6 sm:py-6 lg:px-8' : 'mx-auto px-3 py-3 sm:px-6 sm:py-6 lg:px-8'}>
                  {@render children()}
                </div>
              </div>
            </div>
          </Sidebar.Inset>
        </div>
      </Sidebar.Provider>
      {/if}
    {:else}
      <div class="flex min-h-screen flex-col">
        <header class="bg-white shadow dark:bg-gray-800">
          <div class="mx-auto px-4 sm:px-6 lg:px-8">
            <div class="flex h-16 justify-between">
            <div class="flex shrink-0 items-center">
                <span class="text-xl font-semibold text-gray-900 dark:text-gray-100">{$_(wmsApp.active ? 'vms.app.name' : 'app.name')}</span>
              </div>
              <div class="flex items-center">
                <HeaderPreferencesMenu panelTopClass="top-12" />
              </div>
            </div>
          </div>
        </header>

        <main class="grow">
          <div class="mx-auto px-4 py-8 sm:px-6 lg:px-8">
            {@render children()}
          </div>
        </main>

        <footer class="border-t border-gray-700 bg-gray-800 text-white dark:bg-gray-900">
          <div class="mx-auto px-4 py-6 sm:px-6 lg:px-8">
            <p class="text-center">&copy; {new Date().getFullYear()} {$_('app.name')}</p>
          </div>
        </footer>
      </div>
    {/if}
  </div>
{/if}
